/**
 * POS Bank Card Discounts (module `pos_bank_discounts`).
 *
 * Everything the feature needs lives here so the rest of the POS stays
 * untouched when the module is OFF:
 *   • offers      — companies/{cid}/bankDiscounts/{offerId}
 *   • per-card use — companies/{cid}/bankDiscountUsage/{offerId}__{first6+last4}
 *   • sale log     — companies/{cid}/bankDiscountLog/{transactionId}   (report source)
 *
 * Money rules (agreed with the business):
 *   • The discount % is taken on the bill BEFORE GST; GST is then charged on
 *     the discounted value (the POS spreads it across the lines, like an item
 *     discount, so tax and line values stay consistent).
 *   • Split per offer: shop share % (default 60) + bank share % (default 40).
 *     Sales are booked net (as with any discount); the bank's share is booked
 *     Dr "Bank Discount Claim – <bank>" / Cr "Bank Discount Recovery" until the
 *     bank pays it back (a normal Bank Receipt then clears the claim).
 *   • Only the card's first 6 + last 4 digits are ever stored.
 */
import {
  collection, doc, getDoc, getDocs, addDoc, updateDoc, setDoc, query, where
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { getAccountsCached, invalidateMaster } from './masterDataStore';
import { generateAccountCode } from './accountHelper';
import { localDateStr } from './localDate';

export const BANK_DISCOUNT_MODULE_ID = 'pos_bank_discounts';
export const DEFAULT_SHOP_SHARE = 60;

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
const digits = (s) => String(s || '').replace(/\D/g, '');

export const PK_BANKS = [
  'HBL', 'MCB', 'UBL', 'Meezan Bank', 'Bank Alfalah', 'Allied Bank', 'Standard Chartered',
  'Faysal Bank', 'Askari Bank', 'Bank Al Habib', 'JS Bank', 'Habib Metro', 'Soneri Bank',
  'Bank of Punjab', 'National Bank', 'Dubai Islamic Bank', 'Silk Bank', 'BankIslami', 'Al Baraka'
];
export const CARD_TYPES = ['Credit Card', 'Debit Card', 'Platinum', 'Gold', 'Titanium', 'Signature', 'World', 'Infinite', 'Classic'];
export const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const bankKeyOf = (bankName) => String(bankName || '').trim().toLowerCase().replace(/\s+/g, ' ');
export const offerLabel = (o) => `${o?.bankName || ''} ${o?.cardType || ''}`.trim();
export const maskCard = (first6, last4) => `${digits(first6)}******${digits(last4)}`;

// ─── Offers ────────────────────────────────────────────────────────────────

export const loadBankDiscountOffers = async (companyId) => {
  if (!companyId) return [];
  const snap = await getDocs(collection(db, 'companies', companyId, 'bankDiscounts'));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

const hhmm = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

/** Is this offer usable right now at this branch? Returns '' when OK, else the reason. */
export const offerUnavailableReason = (offer, { now = new Date(), branchId = null } = {}) => {
  if (!offer) return 'No offer';
  if (offer.isActive === false) return 'Offer is inactive';
  const today = localDateStr(now);
  if (offer.validFrom && today < offer.validFrom) return `Starts ${offer.validFrom}`;
  if (offer.validTo && today > offer.validTo) return `Expired ${offer.validTo}`;
  const days = Array.isArray(offer.daysOfWeek) ? offer.daysOfWeek : [];
  if (days.length > 0 && !days.includes(now.getDay())) return 'Not valid today';
  const t = hhmm(now);
  if (offer.timeFrom && offer.timeTo) {
    // Overnight windows (e.g. 22:00–02:00) are supported.
    const inWin = offer.timeFrom <= offer.timeTo
      ? (t >= offer.timeFrom && t <= offer.timeTo)
      : (t >= offer.timeFrom || t <= offer.timeTo);
    if (!inWin) return `Valid ${offer.timeFrom}–${offer.timeTo}`;
  }
  const branches = Array.isArray(offer.branchIds) ? offer.branchIds : [];
  if (branches.length > 0 && branchId && !branches.includes(branchId)) return 'Not valid at this branch';
  return '';
};

export const offersValidNow = (offers, ctx) =>
  (offers || []).filter(o => !offerUnavailableReason(o, ctx))
    .sort((a, b) => offerLabel(a).localeCompare(offerLabel(b)));

/** Card first-6 check against the offer's BIN list (blank list = any card). */
export const cardMatchesOffer = (offer, first6) => {
  const bins = String(offer?.cardBins || '').split(/[\s,]+/).map(digits).filter(Boolean);
  if (bins.length === 0) return true;
  const f = digits(first6);
  return bins.some(b => f.startsWith(b) || b.startsWith(f));
};

// ─── Per-card usage ────────────────────────────────────────────────────────

export const usageDocId = (offerId, first6, last4) => `${offerId}__${digits(first6)}${digits(last4)}`;

/** Read this card's usage for the offer, reduced to today / this month. */
export const readCardUsage = async (companyId, offerId, first6, last4) => {
  const ref = doc(db, 'companies', companyId, 'bankDiscountUsage', usageDocId(offerId, first6, last4));
  const snap = await getDoc(ref);
  const today = localDateStr();
  const month = today.slice(0, 7);
  const d = snap.exists() ? snap.data() : {};
  return {
    dayKey: today,
    monthKey: month,
    dayCount: d.dayKey === today ? (Number(d.dayCount) || 0) : 0,
    monthCount: d.monthKey === month ? (Number(d.monthCount) || 0) : 0,
    monthAmount: d.monthKey === month ? (Number(d.monthAmount) || 0) : 0,
  };
};

/**
 * Check the per-card limits. Returns { ok, error, remainingMonthAmount }.
 * remainingMonthAmount = null when the offer has no monthly amount limit.
 */
export const checkCardLimits = (offer, usage) => {
  const maxDay = num(offer.maxUsesPerDay);
  const maxMonth = num(offer.maxUsesPerMonth);
  const maxAmt = num(offer.maxDiscountPerMonth);
  if (maxDay != null && maxDay > 0 && usage.dayCount >= maxDay) {
    return { ok: false, error: `This card already used the offer ${usage.dayCount} time(s) today (limit ${maxDay}).` };
  }
  if (maxMonth != null && maxMonth > 0 && usage.monthCount >= maxMonth) {
    return { ok: false, error: `This card already used the offer ${usage.monthCount} time(s) this month (limit ${maxMonth}).` };
  }
  let remainingMonthAmount = null;
  if (maxAmt != null && maxAmt > 0) {
    remainingMonthAmount = round2(maxAmt - usage.monthAmount);
    if (remainingMonthAmount <= 0) {
      return { ok: false, error: `This card has used its full monthly discount (${maxAmt}).` };
    }
  }
  return { ok: true, error: '', remainingMonthAmount };
};

/** Max discount allowed on this bill = min(cap per bill, card's remaining monthly amount). */
export const maxDiscountForBill = (offer, remainingMonthAmount) => {
  const caps = [];
  const cap = num(offer.capPerBill);
  if (cap != null && cap > 0) caps.push(cap);
  if (remainingMonthAmount != null) caps.push(remainingMonthAmount);
  return caps.length ? Math.min(...caps) : null;
};

/** Shop / bank split of a discount amount. Bank share is rounded; shop gets the rest. */
export const splitShares = (amount, shopSharePct) => {
  const shopPct = Math.min(100, Math.max(0, Number(shopSharePct ?? DEFAULT_SHOP_SHARE)));
  const bankShareAmount = round2((Number(amount) || 0) * (100 - shopPct) / 100);
  return {
    shopSharePct: shopPct,
    bankSharePct: round2(100 - shopPct),
    bankShareAmount,
    shopShareAmount: round2((Number(amount) || 0) - bankShareAmount),
  };
};

// ─── Line allocation (before GST) ──────────────────────────────────────────

/**
 * Spread a bank discount across already-calculated POS lines (calculatePOSLine
 * output), before GST. Each line's discount grows by its share, its GST is
 * recomputed on the lower value and its total updated — the same way an item
 * discount works. Returns { lines, amount }.
 *
 * spec = { percent, maxAmount } (maxAmount null = no cap)
 */
export const applyBankDiscountToLines = (calculated, spec) => {
  const pct = Number(spec?.percent) || 0;
  const base = (calculated || []).reduce((s, l) => s + Math.max(0, Number(l.netAmount) || 0), 0);
  if (!(pct > 0) || !(base > 0)) return { lines: calculated, amount: 0 };
  let amount = round2(base * pct / 100);
  const max = spec?.maxAmount;
  if (max != null && Number(max) >= 0) amount = Math.min(amount, round2(Number(max)));
  if (!(amount > 0)) return { lines: calculated, amount: 0 };

  // Proportional shares; any rounding remainder goes to the largest line.
  const shares = calculated.map(l => {
    const net = Math.max(0, Number(l.netAmount) || 0);
    return net > 0 ? round2(amount * net / base) : 0;
  });
  const diff = round2(amount - shares.reduce((s, v) => s + v, 0));
  if (diff !== 0) {
    let big = 0;
    calculated.forEach((l, i) => { if ((Number(l.netAmount) || 0) > (Number(calculated[big].netAmount) || 0)) big = i; });
    shares[big] = round2(shares[big] + diff);
  }

  const lines = calculated.map((l, i) => {
    const share = shares[i];
    if (!(share > 0)) return l;
    const netAmount = round2((Number(l.netAmount) || 0) - share);
    const taxRate = Number(l.taxRate) || 0;
    const taxable = l.taxable !== false;
    const taxAmount = taxable && taxRate > 0 ? round2(netAmount * (taxRate / 100)) : 0;
    return {
      ...l,
      bankDiscountAmount: share,
      discountAmount: round2((Number(l.discountAmount) || 0) + share),
      netAmount,
      taxAmount,
      lineTotal: round2(netAmount + taxAmount),
    };
  });
  return { lines, amount };
};

// ─── Accounts (created when an offer is saved) ─────────────────────────────

const findByKey = (accounts, key, extra = {}) =>
  accounts.find(a => a.systemKey === key && Object.entries(extra).every(([k, v]) => a[k] === v)) || null;

const createAccount = async (companyId, parent, data, userId) => {
  const code = await generateAccountCode(companyId, parent.id, parent.code, parent.level);
  const ts = new Date().toISOString();
  const row = {
    code,
    level: (Number(parent.level) || 0) + 1,
    parentId: parent.id,
    path: [...(parent.path || []), parent.id],
    accountType: parent.accountType,
    balanceType: parent.balanceType,
    linkedEntityType: null,
    linkedEntityId: null,
    isActive: true,
    isSystemAccount: false,
    openingBalance: 0,
    currentBalance: 0,
    createdAt: ts,
    updatedAt: ts,
    createdBy: userId || null,
    ...data,
  };
  const ref = await addDoc(collection(db, 'companies', companyId, 'accounts'), row);
  return { id: ref.id, ...row };
};

/**
 * Make sure the claim account for this bank and the recovery income account
 * exist. Found by a marker (not by a fixed code) so a company's own accounts
 * are never taken over; new ones get the next free code under:
 *   112   Receivables  → "Bank Discount Claims" (group) → "Bank Discount Claim - <bank>"
 *   42102 Other Income → "Bank Discount Recovery"
 * Returns { claimAccount, recoveryAccount }.
 */
export const ensureBankDiscountAccounts = async (companyId, bankName, userId) => {
  // Read live (the device cache can be a little behind) but only the few rows
  // needed — the two parents and our own marked accounts — never the whole chart.
  let accounts;
  try {
    const ref = collection(db, 'companies', companyId, 'accounts');
    const [mine, parents] = await Promise.all([
      getDocs(query(ref, where('systemKey', 'in', ['bankDiscountClaims', 'bankDiscountClaim', 'bankDiscountRecovery']))),
      getDocs(query(ref, where('code', 'in', ['112', '42102'])))
    ]);
    accounts = [...mine.docs, ...parents.docs].map(d => ({ id: d.id, ...d.data() }));
  } catch (e) {
    accounts = (await getAccountsCached(companyId)) || [];
  }

  const byCode = (c) => accounts.find(a => String(a.code) === c) || null;
  const receivables = byCode('112');
  const otherIncome = byCode('42102');
  if (!receivables || !otherIncome) {
    throw new Error('Standard accounts 112 (Receivables) / 42102 (Other Income) are missing. Run Repair COA in Chart of Accounts, then save again.');
  }
  let created = false;

  let group = findByKey(accounts, 'bankDiscountClaims');
  if (!group) {
    group = await createAccount(companyId, receivables, {
      name: 'Bank Discount Claims', systemKey: 'bankDiscountClaims', isPostable: false,
      description: "Banks' share of POS card discounts, to be claimed back from each bank",
    }, userId);
    accounts.push(group); created = true;
  }

  const bk = bankKeyOf(bankName);
  let claim = findByKey(accounts, 'bankDiscountClaim', { bankKey: bk });
  if (!claim) {
    claim = await createAccount(companyId, group, {
      name: `Bank Discount Claim - ${String(bankName).trim()}`, systemKey: 'bankDiscountClaim', bankKey: bk,
      isPostable: true, description: `${String(bankName).trim()}'s share of POS card discounts (receivable)`,
    }, userId);
    accounts.push(claim); created = true;
  }

  let recovery = findByKey(accounts, 'bankDiscountRecovery');
  if (!recovery) {
    recovery = await createAccount(companyId, otherIncome, {
      name: 'Bank Discount Recovery', systemKey: 'bankDiscountRecovery', isPostable: true,
      description: "Banks' share of POS card discounts (income)",
    }, userId);
    created = true;
  }

  if (created) { try { invalidateMaster(companyId, 'accounts'); } catch (e) { /* non-fatal */ } }
  const pick = (a) => ({ id: a.id, code: a.code || '', name: a.name || '' });
  return { claimAccount: pick(claim), recoveryAccount: pick(recovery) };
};

// ─── After a sale / return / void ──────────────────────────────────────────

/**
 * Reverse (part of) a bank discount: give the amount (and, for a full reversal,
 * the use) back to the card's monthly limit and note it on the sale's log.
 * For a void/delete the amount is whatever is still un-reversed on the log, so a
 * void after a partial return never gives the same money back twice.
 * Best-effort — never blocks the POS.
 */
export const reverseBankDiscountUsage = async (companyId, bankDiscount, { amount, full = false, transactionId, voided = false, returnDocNumber = null }) => {
  if (!companyId || !bankDiscount?.offerId) return;
  let amt = round2(amount);

  let logRef = null;
  let log = null;
  if (transactionId) {
    try {
      logRef = doc(db, 'companies', companyId, 'bankDiscountLog', transactionId);
      const snap = await getDoc(logRef);
      if (snap.exists()) log = snap.data();
    } catch (e) { console.warn('[bankDiscount] log read failed:', e); }
  }
  if (log) {
    if (log.status === 'Voided') return; // already fully reversed
    const open = round2((Number(log.discountAmount) || 0) - (Number(log.reversedAmount) || 0));
    amt = voided ? Math.max(0, open) : Math.min(amt, Math.max(0, open));
  }

  // Nothing left to give back (e.g. a void after a full return): only mark the log.
  if (!(amt > 0) && log) {
    if (voided && logRef) {
      try { await updateDoc(logRef, { status: 'Voided', updatedAt: new Date().toISOString() }); } catch (e) { /* ignore */ }
    }
    return;
  }

  try {
    const ref = doc(db, 'companies', companyId, 'bankDiscountUsage',
      usageDocId(bankDiscount.offerId, bankDiscount.cardFirst6, bankDiscount.cardLast4));
    const snap = await getDoc(ref);
    if (snap.exists()) {
      const d = snap.data();
      const saleDay = bankDiscount.docDate || log?.docDate || '';
      const saleMonth = String(saleDay).slice(0, 7);
      const patch = { updatedAt: new Date().toISOString() };
      // Only counters of the same month/day as the original sale are given back.
      if (saleMonth && d.monthKey === saleMonth) {
        patch.monthAmount = Math.max(0, round2((Number(d.monthAmount) || 0) - amt));
        if (full) patch.monthCount = Math.max(0, (Number(d.monthCount) || 0) - 1);
      }
      if (full && saleDay && d.dayKey === saleDay) patch.dayCount = Math.max(0, (Number(d.dayCount) || 0) - 1);
      await setDoc(ref, patch, { merge: true });
    }
  } catch (e) { console.warn('[bankDiscount] usage reversal failed:', e); }

  if (logRef && log) {
    try {
      const patch = { reversedAmount: round2((Number(log.reversedAmount) || 0) + amt), updatedAt: new Date().toISOString() };
      if (voided) patch.status = 'Voided';
      if (returnDocNumber) patch.returnDocNumbers = [...(log.returnDocNumbers || []), returnDocNumber];
      await updateDoc(logRef, patch);
    } catch (e) { console.warn('[bankDiscount] log update failed:', e); }
  }
};
